import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EscortForm from "./EscortForm";
import { fetchEscortForm, updateEscortForm } from "../../store/slices/escortFormSlice";

const EditEscortForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { currentEscortForm, isLoading } = useSelector((state) => state.escortForms);

  const [formData, setFormData] = useState({
    patient_name: "",
    cnic_bform: "",
    age_gender: "",
    residential_address: "",
    date_of_admission_request: "",

    applicant_name: "",
    applicant_cnic: "",
    applicant_relationship: "",
    applicant_contact: "",
    applicant_address: "",

    ground_mental_disorder: false,
    ground_harm_self: false,
    ground_harm_others: false,
    ground_unable_care_self: false,
    ground_refuses_voluntary: false,

    signature_applicant_name: "",
    signature_applicant_date: "",

    psychiatrist_name: "",
    psychiatrist_pmdc: "",
    psychiatrist_signature: "",
    psychiatrist_certified_at: "",

    trh_representative_name: "",
    trh_representative_designation: "",
    trh_representative_signature: "",
    trh_representative_date: "",

    witness1_name: "",
    witness1_signature: "",
    witness2_name: "",
    witness2_signature: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      dispatch(fetchEscortForm(id));
    }
  }, [dispatch, id]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes("T")) {
      return dateStr.split("T")[0];
    }
    return dateStr.split(" ")[0];
  };

  const formatDateTimeForInput = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    let formatted = dateTimeStr.replace(" ", "T");
    if (formatted.includes(".")) {
      formatted = formatted.split(".")[0];
    }
    return formatted.substring(0, 16);
  };

  useEffect(() => {
    if (currentEscortForm) {
      setFormData({
        patient_id: currentEscortForm.patient_id || patientId || "",
        case_id: currentEscortForm.case_id || caseId || "",
        patient_name: currentEscortForm.patient_name || "",
        cnic_bform: currentEscortForm.cnic_bform || "",
        age_gender: currentEscortForm.age_gender || "",
        residential_address: currentEscortForm.residential_address || "",
        date_of_admission_request: formatDateForInput(currentEscortForm.date_of_admission_request),

        applicant_name: currentEscortForm.applicant_name || "",
        applicant_cnic: currentEscortForm.applicant_cnic || "",
        applicant_relationship: currentEscortForm.applicant_relationship || "",
        applicant_contact: currentEscortForm.applicant_contact || "",
        applicant_address: currentEscortForm.applicant_address || "",

        ground_mental_disorder: !!currentEscortForm.ground_mental_disorder,
        ground_harm_self: !!currentEscortForm.ground_harm_self,
        ground_harm_others: !!currentEscortForm.ground_harm_others,
        ground_unable_care_self: !!currentEscortForm.ground_unable_care_self,
        ground_refuses_voluntary: !!currentEscortForm.ground_refuses_voluntary,

        signature_applicant_name: currentEscortForm.signature_applicant_name || "",
        signature_applicant_date: formatDateForInput(currentEscortForm.signature_applicant_date),

        psychiatrist_name: currentEscortForm.psychiatrist_name || "",
        psychiatrist_pmdc: currentEscortForm.psychiatrist_pmdc || "",
        psychiatrist_signature: currentEscortForm.psychiatrist_signature || "",
        psychiatrist_certified_at: formatDateTimeForInput(currentEscortForm.psychiatrist_certified_at),

        trh_representative_name: currentEscortForm.trh_representative_name || "",
        trh_representative_designation: currentEscortForm.trh_representative_designation || "",
        trh_representative_signature: currentEscortForm.trh_representative_signature || "",
        trh_representative_date: formatDateForInput(currentEscortForm.trh_representative_date),

        witness1_name: currentEscortForm.witness1_name || "",
        witness1_signature: currentEscortForm.witness1_signature || "",
        witness2_name: currentEscortForm.witness2_name || "",
        witness2_signature: currentEscortForm.witness2_signature || "",
      });
    }
  }, [currentEscortForm, patientId, caseId]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.patient_name) newErrors.patient_name = "Patient Name is required";
    if (!formData.cnic_bform) newErrors.cnic_bform = "CNIC / B-Form is required";
    if (!formData.applicant_name) newErrors.applicant_name = "Applicant Name is required";
    if (!formData.applicant_relationship) newErrors.applicant_relationship = "Relationship is required";
    if (!formData.applicant_contact) newErrors.applicant_contact = "Contact is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill all required fields in the form.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      // Format psychiatrist certified date/time for DB if set
      let certTime = formData.psychiatrist_certified_at;
      if (certTime) {
        certTime = certTime.replace("T", " ");
        if (certTime.includes(".")) {
          certTime = certTime.split(".")[0];
        } else if (certTime.includes("+")) {
          certTime = certTime.split("+")[0];
        } else if (certTime.endsWith("Z")) {
          certTime = certTime.substring(0, certTime.length - 1);
        }
        certTime = certTime.trim();
        if (certTime.length === 16) {
          certTime = `${certTime}:00`;
        }
        if (certTime.length > 19) {
          certTime = certTime.substring(0, 19);
        }
      } else {
        certTime = null;
      }

      const payload = {
        ...formData,
        psychiatrist_certified_at: certTime,
      };

      await dispatch(updateEscortForm({ id, formData: payload })).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Escort form updated successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // Stay on edit page

    } catch (err) {
      let errorMessage = "Failed to update Escort form";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (typeof err === "object" && err !== null) {
        errorMessage = Object.values(err).flat().join("\n");
      }
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        <div className="card">
          <div className="card-header border-bottom">
            <h5 className="mb-0">Edit Involuntary Admission / Assisted Pick-Up Authorization Form</h5>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <EscortForm
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
                <div className="row px-4 pb-4">
                  <div className="col-md-2 ms-auto">
                    <button type="submit" className="btn btn-primary w-100 sticky-form-submit">
                      <i className="ri-save-3-line me-1"></i> Update
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditEscortForm;
